import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { LogoMark, WhatsAppIcon } from "../components/Logo";
import { ListingCard } from "../components/Listings";
import { getListings } from "../lib/api";
import { waLink } from "../lib/brand";

const DEAL_FILTERS = ["All", "Sale", "Purchase", "Rent"];

export default function Properties() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deal, setDeal] = useState("All");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    getListings().then(setListings).catch(() => setListings([])).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, []);

  const categories = ["All", ...Array.from(new Set(listings.map((l) => l.category)))];
  const filtered = listings.filter(
    (l) => (deal === "All" || l.deal_type === deal) && (category === "All" || l.category === category)
  );

  return (
    <div className="min-h-screen bg-[#faf9f5] text-[#131311]">
      <header className="sticky top-0 z-40 bg-[#faf9f5]/85 backdrop-blur-xl border-b border-[#e7e3d7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="properties-logo">
            <LogoMark className="h-9 w-9" />
            <span className="flex flex-col leading-none">
              <span className="font-serif text-lg font-semibold tracking-tight">Laxmi Jiwan</span>
              <span className="text-[9px] tracking-[0.42em] uppercase text-[#9a7b38] font-medium mt-0.5">
                Properties · Hisar
              </span>
            </span>
          </a>
          <a
            href="/"
            className="inline-flex items-center gap-2 rounded-full border border-[#e0dccf] px-5 py-2.5 text-sm font-medium text-[#52514c] hover:text-[#131311] hover:border-[#c5a059] transition-colors"
            data-testid="properties-back-home"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </a>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-14 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#c5a059]" />
            <span className="text-xs tracking-[0.34em] uppercase text-[#9a7b38] font-semibold">
              All Properties
            </span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1]">
            Every property we're <span className="italic text-gold-gradient">dealing in.</span>
          </h1>
          <p className="mt-4 text-[#52514c] max-w-xl">
            Browse our complete portfolio across Hisar. Tap any property to enquire instantly on WhatsApp.
          </p>
        </motion.div>

        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="properties-deal-filters">
            {DEAL_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setDeal(f)}
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
                  deal === f
                    ? "bg-[#131311] text-[#faf9f5]"
                    : "border border-[#e0dccf] text-[#52514c] hover:border-[#c5a059] hover:text-[#131311]"
                }`}
                data-testid={`properties-deal-${f.toLowerCase()}`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="properties-category-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`properties-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-xs font-medium transition-all duration-300 ${
                  category === c
                    ? "bg-[#9a7b38] text-white"
                    : "border border-[#e0dccf] text-[#6b675c] hover:border-[#c5a059]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mt-16 grid place-items-center py-10" data-testid="properties-loading">
            <Loader2 className="h-6 w-6 animate-spin text-[#9a7b38]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-16 text-center text-[#8a8578]" data-testid="properties-empty">
            No properties match your filters right now.
          </div>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center sm:justify-items-stretch">
            {filtered.map((l) => (
              <div key={l.id} className="w-full max-w-[340px]">
                <ListingCard l={l} />
              </div>
            ))}
          </div>
        )}
      </section>

      <a
        href={waLink("Hello Laxmi Jiwan Properties, I'm browsing your properties in Hisar and would like some help.")}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full grid place-items-center bg-[#25d366] text-white shadow-[0_12px_30px_rgba(37,211,102,0.4)] lj-pulse"
        data-testid="properties-whatsapp-float"
        aria-label="Chat on WhatsApp"
      >
        <WhatsAppIcon className="h-7 w-7" />
      </a>
    </div>
  );
}
